<template>
  <v-form ref="form" v-model="valid">
    <v-dialog v-model="shouldShowModal" width="500">
      <v-card class="modal-content">
        <v-card-title class="blue--text">Export Profiles</v-card-title>
        <v-card-text>
          <p>The current search criteria will be used to export profiles to a 7zip encrypted file.</p>
          <p>
            Please enter a password or use the auto-generated password. You will need this password to open the 7zip
            file.
          </p>
          <v-text-field id="textToCopy" label="Password" v-model="password" :rules="rules.password">
            <template v-slot:append>
              <v-btn @click="copyText" icon><v-icon>mdi-content-copy</v-icon></v-btn>
              <v-btn @click="password = generatePassword()" icon><v-icon>mdi-refresh</v-icon></v-btn>
            </template>
            <template v-slot:append-outer>
              <v-tooltip right>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon v-bind="attrs" v-on="on"><v-icon color="orange">mdi-help-box</v-icon></v-btn>
                </template>
                <div id="password-tooltip">
                  Passwords should be at least 16 characters long with a mix of alphanumeric characters
                </div>
              </v-tooltip>
            </template>
          </v-text-field>
          <v-alert class="font-weight-bold" type="warning" icon="mdi-alert" color="deep-orange accent-3" dense text>
            Make sure you copy your password as it cannot be recovered after the export is completed.
          </v-alert>
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn class="font-weight-bold" color="red" text @click.stop="shouldShowModal = false">Cancel</v-btn>
          <v-btn class="font-weight-bold" color="blue" text @click.stop="exportProfiles" :disabled="!valid">
            Submit
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-form>
</template>

<script>
import {
  ALERT_TYPE_ERROR,
  ALERT_TYPE_SUCCESS,
  MIN_EXPORT_PASSWORD_LENGTH,
  PROFILE_EXPORT_REQUEST_SUBMITTED,
} from '@/constants';
import ClientMixin from '@/mixins/ClientMixin';
import { isValidExportPassword, required } from '@/validation-rules';
import { mapActions } from 'vuex';
import { Models } from '@helloworld/analytics-client-js';
import PasswordGenerator from 'generate-password';

export default {
  name: 'ExportModal',
  mixins: [ClientMixin],
  props: {
    criteria: {
      type: Object,
      required: true,
    },
    promotionHash: {
      type: String,
      required: true,
    },
    showModal: {
      type: Boolean,
      required: true,
    },
  },

  data() {
    return {
      password: this.generatePassword(),
      rules: {
        password: [required, isValidExportPassword],
      },
      shouldShowModal: this.showModal,
      valid: false,
    };
  },

  created() {
    this.initComponent();
  },

  watch: {
    showModal() {
      if (this.showModal) {
        this.password = this.generatePassword();
      }
      this.shouldShowModal = this.showModal;
    },

    shouldShowModal() {
      this.$emit('update:showModal', this.shouldShowModal);
    },
  },

  methods: {
    ...mapActions(['alertMessage']),

    copyText() {
      let textToCopy = document.getElementById('textToCopy');
      textToCopy.select();

      // There does not seem to be a standard for replacement. This thing still works fine.
      // noinspection JSDeprecatedSymbols
      document.execCommand('copy');
    },

    exportProfiles() {
      const criteria = { ...this.criteria };
      const { ProfileExportJobRequest } = Models;
      const profileExportJobRequest = new ProfileExportJobRequest(this.password, criteria);

      this.client.profiles
        .submitExport(this.promotionHash, profileExportJobRequest)
        .then(() => {
          this.alertMessage([{ message: PROFILE_EXPORT_REQUEST_SUBMITTED, variant: ALERT_TYPE_SUCCESS }]);
          this.shouldShowModal = false;
        })
        .catch((error) => {
          this.alertMessage([{ message: error.message, variant: ALERT_TYPE_ERROR }]);
          this.shouldShowModal = false;
        });
    },

    generatePassword() {
      return PasswordGenerator.generate({
        length: MIN_EXPORT_PASSWORD_LENGTH,
        numbers: true,
        symbols: true,
      });
    },

    initComponent() {
      this.getClient()
        .then((client) => {
          this.client = client;
        })
        .catch((error) => {
          this.alertMessage([{ message: error.message, variant: ALERT_TYPE_ERROR }]);
        });
    },
  },
};
</script>

<style>
.modal-content {
  /* restrict scrollbars from showing */
  overflow-x: hidden;
  overflow-y: hidden;
}

#password-tooltip {
  width: 400px;
}

.processing-text-span {
  color: #212121;
  cursor: default;
}
</style>
